/**
 * Walking an api2 listing a page at a time, and the table footer that goes with it.
 *
 * Every Postgres-backed listing (`/members`, `/teams`, `/boards`) pages by an
 * opaque cursor and says whether there is more, never how many there are. A
 * page is reached by walking, and the way back is the cursors already used.
 * These helpers are that walk, and the numbers Vuetify's own footer
 * (`v-data-footer`) needs to draw it like every other table in the app.
 */

/**
 * Where a walk has got to. The endpoint pages by cursor, so "page 3" is not a
 * thing anyone can ask for: a page is reached by walking, and the way back is
 * the cursors already used.
 *
 * `cursor` produced the page on screen, and null means the first page.
 * `previous` is the stack of cursors behind it, so Back is a pop rather than
 * arithmetic. `next` is what the endpoint handed back, and `hasMore` says
 * whether it leads anywhere. `startPage` is the number of the page the walk
 * began on: 1 from the top, or the remembered page a restored walk resumes at,
 * whose stack is not stored.
 */
export interface CursorWalk {
	cursor: string | null;
	previous: string[];
	next: string | null;
	hasMore: boolean;
	startPage: number;
}

export const CURSOR_WALK_START: CursorWalk = {
	cursor: null,
	previous: [],
	next: null,
	hasMore: false,
	startPage: 1,
};

/** Back to the first page, keeping nothing. Any filter or sort change does this. */
export function restartWalk(): CursorWalk {
	return { ...CURSOR_WALK_START };
}

/** What the endpoint answered, recorded against the page it answered for. */
export function walkAfterPage(walk: CursorWalk, page: { nextCursor: string | null; hasMore: boolean }): CursorWalk {
	return { ...walk, next: page.nextCursor, hasMore: page.hasMore };
}

/** Whether a Back button has anywhere to go. */
export function canWalkBack(walk: CursorWalk): boolean {
	return walk.previous.length > 0;
}

/** Whether a Next button has anywhere to go. */
export function canWalkForward(walk: CursorWalk): boolean {
	return walk.hasMore && !!walk.next;
}

/**
 * One page forward: the cursor on screen joins the stack, and the one the
 * endpoint handed back becomes current. Returns the same walk when there is
 * nowhere to go, so a double click cannot skip a page.
 */
export function walkForward(walk: CursorWalk): CursorWalk {
	if (!canWalkForward(walk)) return walk;
	return {
		cursor: walk.next,
		previous: [...walk.previous, walk.cursor as string],
		next: null,
		hasMore: false,
		startPage: walk.startPage,
	};
}

/**
 * One page back, by popping the stack rather than by asking for a page number.
 * `next` is set to the cursor we are leaving, so the forward button still
 * works before the refetch lands.
 */
export function walkBack(walk: CursorWalk): CursorWalk {
	if (!canWalkBack(walk)) return walk;
	const previous: string[] = walk.previous.slice(0, -1);
	return {
		cursor: walk.previous[walk.previous.length - 1],
		previous,
		next: walk.cursor,
		hasMore: true,
		startPage: walk.startPage,
	};
}

/**
 * How far in the walk we are: the page it started on plus the pages walked
 * since. There is no total, so this counts pages behind rather than pages of a
 * known number. The endpoint cannot say "of 12" without counting the whole set
 * on every request.
 */
export function walkPageNumber(walk: CursorWalk): number {
	return (walk.startPage || 1) + walk.previous.length;
}

/**
 * The `pagination` Vuetify's table footer (`v-data-footer`) draws, for a walk.
 * A cursor-paged table shows the same footer as every other table in the app,
 * but that footer expects a known total, which a cursor does not have. The numbers are
 * true except `itemsLength`: it is the total only on the last page, where the
 * walk has seen it. Before that nobody has counted, and it is merely large
 * enough to turn the footer's Next arrow on (Next goes off once page times page
 * size reaches it). The footer's text comes from `walkFooterPageText`, never
 * from these numbers.
 */
export interface WalkFooterPagination {
	page: number;
	itemsPerPage: number;
	// Zero-based, as Vuetify counts: the first row on screen is pageStart + 1.
	pageStart: number;
	pageStop: number;
	pageCount: number;
	itemsLength: number;
}

export function walkFooterPagination(walk: CursorWalk, itemsPerPage: number, rowCount: number): WalkFooterPagination {
	const page: number = walkPageNumber(walk);
	const pageStart: number = (page - 1) * itemsPerPage;
	const pageStop: number = pageStart + rowCount;
	const more: boolean = canWalkForward(walk);
	return {
		page,
		itemsPerPage,
		pageStart,
		pageStop,
		pageCount: more ? page + 1 : page,
		itemsLength: more ? page * itemsPerPage + 1 : pageStop,
	};
}

/**
 * The footer's "1-25 of 48". The total is known only on the last page, so the
 * pages before it say "of many", the usual wording for a count nobody has
 * taken. A page with no rows shows Vuetify's own placeholder.
 */
export function walkFooterPageText(walk: CursorWalk, pagination: WalkFooterPagination): string {
	if (pagination.pageStop <= pagination.pageStart) return '\u2013';
	const total: string = canWalkForward(walk) ? 'many' : String(pagination.pageStop);
	return `${pagination.pageStart + 1}-${pagination.pageStop} of ${total}`;
}

/**
 * How long after a mutation the pages wait before asking Postgres again. The
 * mirror trails Firestore: the watcher only picks up sync records older than
 * WATCHER_LAG_WINDOW_SECONDS = 10, polls every 5 seconds, and then has to
 * upsert the row (restServer/runnit/management/commands/watch_firebase_sql_syncs.py).
 * A refetch fired any sooner returns the list as it was before the write. The
 * rows already on screen reflect the change at once through their bindings,
 * so nothing else is needed in the meantime.
 */
export const MIRROR_LAG_REFETCH_MS: number = 20000;

export interface FetchFailureInput {
	// The request that failed was the catch-up refetch nobody asked for.
	background: boolean;
	// A request the user DID ask for (a page, sort, or filter change) was in
	// flight when this one superseded it, so this one answers for it too.
	supersededForeground: boolean;
	hasRows: boolean;
}

/**
 * Whether a failed fetch may leave the table as it is. Only a pure background
 * refetch may: its rows are the right rows and are live through their
 * bindings. A background refetch that superseded a user-initiated request
 * inherits that request's duty, because the rows on screen belong to the
 * previous page, sort, or filter and must not sit under the new one.
 */
export function fetchFailureKeepsRows(input: FetchFailureInput): boolean {
	return input.background && !input.supersededForeground && input.hasRows;
}

/**
 * One page of any api2 listing, as its client hands it over. Every
 * Postgres-backed client (`membersApi`, `boardsApi`, `teamsApi`) answers in this
 * shape, so the paging in CursorListMixin is written once.
 */
export interface ListPage<R> {
	rows: R[];
	// Send back as `cursor` for the next page. Null on the last one.
	nextCursor: string | null;
	hasMore: boolean;
}

export interface ListFetchFailure {
	ok: false;
	// `stale-cursor` is the endpoint's INVALID_CURSOR: the position resumed
	// from no longer belongs to this listing, and the table starts over.
	reason: 'network' | 'unauthorized' | 'forbidden' | 'failed' | 'stale-cursor';
	// The server's structured error envelope ({ error: { code, message } }).
	code?: string;
	message?: string;
}

export type ListFetchResult<R> =
	| { ok: true; page: ListPage<R> }
	| ListFetchFailure;

/**
 * The failure, or null when the fetch worked. A function rather than a test of
 * `result.ok` at the call site: `web` compiles with `strictNullChecks: false`,
 * where `if (!result.ok)` does not narrow the union (see `membersFetchFailure`).
 */
export function listFetchFailure<R>(result: ListFetchResult<R>): ListFetchFailure | null {
	return result.ok === true ? null : result;
}

/**
 * What the snackbar says about a failed fetch: the table's own line for the
 * reason, except a domain lockdown (`DOMAIN_LOCKED`), whose server message names
 * the company to switch to, which the generic access line would hide.
 */
export function listFetchFailureMessage(failure: ListFetchFailure, messageByReason: Record<string, string>): string {
	if (failure.code === 'DOMAIN_LOCKED' && failure.message) return failure.message;
	return messageByReason[failure.reason] || messageByReason.failed;
}
